import { type FC, Fragment } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import * as SelectPrimitive from '@radix-ui/react-select'

import { FormControl } from '@/components/atoms/Form/FormControl'
import { FormField } from '@/components/atoms/Form/FormField'
import { FormItem } from '@/components/atoms/Form/FormItem'
import { Icons } from '@/components/atoms/Icons/Icons'
import { type Promotion } from '@/components/model'
import { cn } from '@/lib/utils'

import { type RegionsMilestonesFormData } from './RegionsMilestonesFormZod'

export interface RegionsMilestonesFormTableProps {
  promotions?: Promotion[]
  currentOperatingDate?: string
}

export const RegionsMilestonesFormTable: FC<RegionsMilestonesFormTableProps> = ({
  promotions = [],
  currentOperatingDate,
}) => {
  const intl = useIntl()
  const { watch } = useFormContext<RegionsMilestonesFormData>()
  const milestones = watch()

  const sortedDates = Object.keys(milestones).sort()

  if (sortedDates.length === 0) {
    return <div className="p-6 text-center text-gray-500">{intl.formatMessage({ id: 'regionMilestones.empty' })}</div>
  }

  return (
    <div className="grid grid-cols-[80px_1fr] gap-4">
      {sortedDates.map((date) => {
        const isCurrentOperatingDate = currentOperatingDate === date

        return (
          <Fragment key={date}>
            <div className="flex items-center">
              <p className="text-sm font-medium text-gray-900">
                {intl.formatDate(date, {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                  timeZone: 'UTC',
                })}
              </p>
            </div>
            <div className="flex items-center">
              <FormField<RegionsMilestonesFormData, string>
                name={date}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormItem className="flex w-full items-center py-0">
                    <FormControl>
                      <SelectPrimitive.Root value={value ?? ''} onValueChange={onChange} {...field}>
                        <SelectPrimitive.Trigger
                          className={cn(
                            'flex h-8 w-full items-center justify-between gap-2 rounded-sm border px-3 text-sm select-none focus:ring-0 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
                            isCurrentOperatingDate
                              ? 'border-[#0e9f6e] bg-[#def7ec] text-[#111928]'
                              : value
                                ? 'border-gray-200 bg-white text-black'
                                : 'border-gray-200 bg-gray-50 text-gray-500'
                          )}>
                          <SelectPrimitive.Value
                            placeholder={intl.formatMessage({ id: 'regionMilestones.selectPlaceholder' })}
                          />
                          <SelectPrimitive.Icon asChild>
                            <Icons.chevronDown className="size-4 stroke-current stroke-2" />
                          </SelectPrimitive.Icon>
                        </SelectPrimitive.Trigger>
                        <SelectPrimitive.Portal>
                          <SelectPrimitive.Content
                            className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-32 overflow-hidden rounded-[8px] bg-white text-gray-700 shadow-xl"
                            position="popper">
                            <SelectPrimitive.Viewport className="h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) p-0">
                              {promotions.map((promotion) => (
                                <SelectPrimitive.Item
                                  key={promotion._id}
                                  value={promotion._id}
                                  className="relative flex w-full cursor-pointer items-center px-4 py-2 text-base leading-[150%] outline-hidden select-none focus:bg-blue-100 data-disabled:pointer-events-none data-disabled:opacity-50">
                                  <SelectPrimitive.ItemText>
                                    {promotion.partnerName} ({promotion.title})
                                  </SelectPrimitive.ItemText>
                                </SelectPrimitive.Item>
                              ))}
                            </SelectPrimitive.Viewport>
                          </SelectPrimitive.Content>
                        </SelectPrimitive.Portal>
                      </SelectPrimitive.Root>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </Fragment>
        )
      })}
    </div>
  )
}
